import { DownOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { Button, Dropdown, Flex, Grid, Typography } from 'antd';
import { Header } from 'antd/es/layout/layout';
import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { FaMoon } from 'react-icons/fa';
import { MdSunny } from 'react-icons/md';
import Flag from 'react-world-flags';
import '../../Assets/Styles/Header.module.css';
import logo from '../../Assets/Images/Logo_Math_ComputerScience.png';
import { useTheme } from '../../context/ThemeContext';

const { useBreakpoint } = Grid;

const FLAG_WIDTH = 25;

const LANGUAGES = [
  { key: 'ro', label: 'RO', flag: 'RO' },
  { key: 'hu', label: 'HU', flag: 'HU' },
  { key: 'en', label: 'EN', flag: 'GB' },
  { key: 'de', label: 'DE', flag: 'DE' },
];

const flagOf = (language: string | undefined): ReactNode => {
  const flag = LANGUAGES.find((item) => item.key === language)?.flag ?? 'GB';
  return <Flag width={FLAG_WIDTH} height="auto" code={flag} />;
};

// The header of the login page, without what needs the API (the user, the study year, the notifications): the
// maintenance page must not send any request while the database is worked on.
export const MaintenanceHeader = (): ReactElement => {
  const { t, i18n } = useTranslation();
  const { isDarkMode, toggleTheme } = useTheme();
  const screens = useBreakpoint();

  const languageMenu: MenuProps = {
    items: LANGUAGES.map(({ key, label }) => ({
      key,
      label,
      icon: flagOf(key),
    })),
    onClick: ({ key }) => {
      i18n.changeLanguage(key);
      localStorage.setItem('language', key);
    },
  };

  return (
    <Header
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 1,
        height: 'auto',
        width: '100%',
        background: isDarkMode ? '#01033c' : '#214175',
        backdropFilter: 'blur(10px)',
        padding: 0,
        WebkitBackdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        lineHeight: 'initial',
        boxShadow: '0 4px 14px rgba(0, 0, 0, 0.4)',
      }}
    >
      <nav style={{ width: '100%', boxSizing: 'border-box' }}>
        <Flex align="middle" justify="space-between">
          {/* keeps the logo in the middle, like on the login page */}
          <Flex align="middle" style={{ padding: '0 5px' }} />

          <Flex align="middle" justify="center">
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                paddingLeft: 2,
                paddingTop: 3,
                paddingBottom: 3,
              }}
            >
              <a
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
                href="/"
              >
                <img style={{ objectFit: 'contain' }} src={logo} alt="logo" />
              </a>
              {screens.lg && (
                <Typography.Title
                  level={3}
                  style={{
                    margin: 0,
                    marginLeft: 10,
                    color: '#ffffff',
                    fontSize: '18px',
                    fontWeight: 600,
                    letterSpacing: '0.5px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t('header.facultyName')}
                </Typography.Title>
              )}
            </div>
          </Flex>

          <Flex align="center" gap={10} style={{ padding: '0 5px' }}>
            <Button
              type="text"
              onClick={toggleTheme}
              aria-label={t('maintenance.switchTheme')}
              icon={
                isDarkMode ? (
                  <MdSunny style={{ fontSize: 20, color: 'white' }} />
                ) : (
                  <FaMoon style={{ fontSize: 20, color: 'white' }} />
                )
              }
            />

            <Dropdown menu={languageMenu}>
              <Button
                type="text"
                style={{ paddingLeft: '15px', paddingRight: '0px' }}
              >
                {flagOf(i18n.language)}
                <DownOutlined style={{ fontSize: '10px', color: 'white' }} />
              </Button>
            </Dropdown>
          </Flex>
        </Flex>
      </nav>
    </Header>
  );
};
